import React from "react";

import styled from "styled-components";
import { TbCloudUpload, TbInfoCircle } from "react-icons/tb";
import { Box, Flex, Image, Text } from "rebass/styled-components";
import { useState, useRef, useEffect, useCallback } from "react";
import { useRecoilState } from "recoil";
import { AiFillCloseCircle } from "react-icons/ai";
import axios from "axios";
import { useLazyQuery } from "@apollo/client";

import theme from "theme/themeThree";
import { BaseSection } from "../styledComponents";
import { Toggle } from "MVVM/Components/Toggle";
import { LoadingSpinner } from "MVVM/Components/Lottie";
import {
  QuickEditPreferredFinalTouches,
  QuickEditPreferredThumbnail,
} from "MVVM/State/quickEdit";
import { usePortal } from "MVVM/Hooks/usePortal";
import { SignatureSelectorModal } from "MVVM/Components/Signatures";
import { analytics } from "MVVM/Analytics";
import { IQuickEditMoment } from "../@types";
import { useUser } from "MVVM/Hooks/useUser";
import { Button } from "../../Button";
import { Tooltip } from "MVVM/Components/Tooltip";
import { GET_MY_SIGNATURE_QUICK_EDIT } from "MVVM/GraphQL/quickEdit";
import { TClipCard } from "MVVM/State/loadoutSelector";

export const FinalTouches = ({
  selectedFinalTouches,
  setSelectedFinalTouches,
  setThumbnailKey,
  mediaUploadUrl,
  isOwnMoment,
  moment,
  hideSignature,
  disablePreferredTouches,
}: {
  selectedFinalTouches: string[];
  setSelectedFinalTouches: (finalTouches: string[]) => void;
  setThumbnailKey: (thumbnailKey: string) => void;
  mediaUploadUrl?: {
    fields: {
      name: string;
      value: string;
    }[];
    key: string;
    url: string;
  };
  isOwnMoment: boolean;
  moment: IQuickEditMoment;
  hideSignature?: boolean;
  disablePreferredTouches?: boolean;
}) => {
  const { allstarUser } = useUser();
  const [selectedFileUrl, setSelectedFileUrl] = useState<string | null>(null);
  const [preferredThumbnail, setPreferredThumbnail] = useRecoilState(
    QuickEditPreferredThumbnail,
  );
  const [preferredFinalTouches, setPreferredFinalTouches] = useRecoilState(
    QuickEditPreferredFinalTouches,
  );
  const [userHasSignature, setUserHasSignature] = useState<boolean | null>(
    null,
  );
  const [userHasSignatureMusic, setUserHasSignatureMusic] = useState<
    boolean | null
  >(null);
  const [getMySignature] = useLazyQuery(GET_MY_SIGNATURE_QUICK_EDIT, {
    fetchPolicy: "network-only",
  });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState<boolean>(false);
  const isSecondary = ["secondary", "secondary_fail"].includes(moment.type);
  const signatureDisabled = !isOwnMoment;
  const rapidFireDisabled = isSecondary;
  const contextCamsDisabled = isSecondary;

  const isCounterStrike = moment.game === "CS2";

  const fetchUserSignature = useCallback(() => {
    if (allstarUser?.user?._id) {
      getMySignature({
        variables: {
          user: allstarUser.user._id,
          signature: true,
        },
      }).then(({ data }) => {
        const hasSignature =
          (data?.getLoadouts?.metadataCustomStyleSelection?.length ?? 0) > 0;

        const hasMusicSelected =
          hasSignature &&
          data.getLoadouts?.clipCards?.some(
            (clipCard: TClipCard) => clipCard.card.effect === "FACET_LOOKUP",
          );

        setUserHasSignature(hasSignature);
        setUserHasSignatureMusic(hasMusicSelected);
      });
    }
  }, [allstarUser?.user?._id, getMySignature]);

  useEffect(() => {
    if (allstarUser?.loggedIn && userHasSignature === null) {
      fetchUserSignature();
    }
  }, [allstarUser?.loggedIn, userHasSignature, fetchUserSignature]);

  const {
    portalOpen: signatureSelectorOpen,
    setPortalOpen: setSignatureSelectorOpen,
    Portal: SignatureSelectorPortal,
  } = usePortal(
    <SignatureSelectorModal
      onSave={() => {
        setSignatureSelectorOpen(false);
        fetchUserSignature();
      }}
      onCloseClick={() => {
        setSignatureSelectorOpen(false);
      }}
    />,
  );

  useEffect(() => {
    if (preferredThumbnail && !disablePreferredTouches) {
      setSelectedFileUrl(preferredThumbnail);
      setThumbnailKey(preferredThumbnail);
    }
  }, [preferredThumbnail, setThumbnailKey, disablePreferredTouches]);

  useEffect(() => {
    if (disablePreferredTouches) return;
    let preloadedTouches = preferredFinalTouches;
    if (signatureDisabled)
      preloadedTouches = preloadedTouches.filter(
        (t) => t !== "signature" && t !== "music",
      );
    if (rapidFireDisabled)
      preloadedTouches = preloadedTouches.filter((t) => t !== "rapidFire");
    if (contextCamsDisabled)
      preloadedTouches = preloadedTouches.filter((t) => t !== "contextCams");
    if (userHasSignature === false)
      preloadedTouches = preloadedTouches.filter((t) => t !== "signature");
    setSelectedFinalTouches(preloadedTouches);
  }, [
    preferredFinalTouches,
    signatureDisabled,
    rapidFireDisabled,
    contextCamsDisabled,
    setSelectedFinalTouches,
    disablePreferredTouches,
    userHasSignature,
  ]);

  const { REACT_APP_S3_CLIP_BUCKET } = process.env;

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      setUploading(true);
      const fileUrl = URL.createObjectURL(file);
      const formData = new FormData();

      for (const field of mediaUploadUrl?.fields ?? []) {
        formData.append(field.name, field.value ?? "");
      }
      formData.append("file", file);

      try {
        await axios.post(mediaUploadUrl?.url ?? "", formData);

        setSelectedFileUrl(fileUrl);
        if (mediaUploadUrl?.key) {
          setThumbnailKey(`${mediaUploadUrl.url}/${mediaUploadUrl.key}`);
        }
        setUploading(false);
      } catch (error) {
        setUploading(false);
      }
    }
  };

  const handleThumbnailClick = () => {
    fileInputRef.current?.click();
  };

  const handleToggle = (touch: string) => {
    if (selectedFinalTouches.includes(touch)) {
      setPreferredFinalTouches(
        preferredFinalTouches.filter((t) => t !== touch),
      );
      setSelectedFinalTouches(selectedFinalTouches.filter((t) => t !== touch));
    } else {
      if (touch === "signature" && userHasSignature === false) {
        analytics.track("Signature - Open Modal", {
          where: "Quick Edit",
          type: "First Time Setup",
        });
        setSignatureSelectorOpen(true);
      }
      setPreferredFinalTouches([...preferredFinalTouches, touch]);
      setSelectedFinalTouches([...selectedFinalTouches, touch]);
    }
  };

  const {
    portalOpen: thumbnailSelectorOpen,
    setPortalOpen: setThumbnailSelectorOpen,
    Portal: ThumbnailSelectorPortal,
  } = usePortal(
    <ThumbnailSection>
      <Text
        variant="text.labelSmall"
        style={{
          width: "100%",
          justifyContent: "start",
          color: theme.colors.chalk,
        }}
      >
        Clip Thumbnail:
      </Text>

      <input
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        ref={fileInputRef}
        style={{ display: "none" }}
      />

      <Flex style={{ position: "relative", width: "100%" }}>
        <ThumbnailArea
          onClick={handleThumbnailClick}
          showBorder={!selectedFileUrl}
        >
          {selectedFileUrl ? (
            <PreviewThumbnail src={selectedFileUrl} />
          ) : (
            <>
              {uploading ? (
                <LoadingSpinner height={30} width={30} />
              ) : (
                <Flex
                  flexDirection={"column"}
                  alignItems={"center"}
                  style={{
                    gap: 8,
                  }}
                >
                  <UploadIcon size={30} />
                  <Text variant="text.labelMedium" color={theme.colors.chalk}>
                    Customize Your Thumbnail
                  </Text>
                  <Text
                    variant="text.labelXXSmall"
                    color={theme.colors.chalk}
                    textAlign={"center"}
                    maxWidth={300}
                  >
                    Upload an optional PNG, JPEG or GIF as a custom thumbnail,
                    or we'll use your Signature or current loadout.
                  </Text>
                </Flex>
              )}
            </>
          )}
        </ThumbnailArea>
      </Flex>
      <Flex
        style={{
          position: "relative",
          width: "100%",
          gap: 12,
          justifyContent: "center",
          alignItems: "center",
          paddingTop: 12,
        }}
      >
        <Button
          classification="roundedFilledAlt"
          size="small"
          onClick={() => {
            setPreferredThumbnail("");
            setSelectedFileUrl(null);
            setThumbnailKey("");
            setThumbnailSelectorOpen(false);
          }}
          flex={true}
        >
          Cancel
        </Button>
        <Button
          classification="roundedFilled"
          size="small"
          flex={true}
          disabled={!selectedFileUrl}
          onClick={() => setThumbnailSelectorOpen(false)}
        >
          Save
        </Button>
      </Flex>
    </ThumbnailSection>,
  );

  return (
    <>
      {signatureSelectorOpen && SignatureSelectorPortal}
      {thumbnailSelectorOpen && ThumbnailSelectorPortal}
      <Section>
        <Text
          variant="text.labelSmall"
          style={{
            width: "100%",
            justifyContent: "start",
            color: theme.colors.chalk,
          }}
        >
          Choose your finishing touches:
        </Text>
        <ToggleContainer>
          <ToggleColumn>
            <ToggleRow>
              <FeatureTooltip
                title="Set Custom Thumbnail"
                description="By default, your clips will have a thumbnail selected in your Signature or loadout. Upload your own to add some extra personalization."
              >
                <Flex alignItems="center" style={{ gap: 4 }}>
                  <Text variant="text.labelSmall">Set Custom Thumbnail</Text>
                  <InfoTooltipIcon />
                </Flex>
              </FeatureTooltip>
              <Flex
                style={{
                  justifyContent: "start",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                {selectedFileUrl ? (
                  <RemoveText
                    variant="text.labelSmall"
                    color="alert"
                    onClick={() => {
                      setPreferredThumbnail("");
                      setSelectedFileUrl(null);
                      setThumbnailKey("");
                    }}
                  >
                    Remove
                    <AiFillCloseCircle
                      style={{
                        width: 18,
                        height: 18,
                        marginLeft: 8,
                        verticalAlign: "middle",
                        color: theme.colors.alert,
                      }}
                    />
                  </RemoveText>
                ) : (
                  <SignatureText
                    color="chalk"
                    variant="text.labelSmall"
                    onClick={() => {
                      analytics.track("Thumbnail - Open Modal", {
                        where: "Quick Edit",
                        type: "Customize",
                      });
                      setThumbnailSelectorOpen(true);
                    }}
                  >
                    Upload
                  </SignatureText>
                )}
              </Flex>
            </ToggleRow>
          </ToggleColumn>

          {!hideSignature && (
            <>
              <ToggleColumn>
                <ToggleRow>
                  <FeatureTooltip
                    title="Add My Signature"
                    description="Signatures allow you to add a personalized intro/nameplate/outro to your clips, to show off your stats to viewers."
                  >
                    <Flex alignItems="center" style={{ gap: 4 }}>
                      <Text variant="text.labelSmall">Add My Signature</Text>
                      <InfoTooltipIcon />
                    </Flex>
                  </FeatureTooltip>
                  <Toggle
                    width={48}
                    checked={selectedFinalTouches.includes("signature")}
                    disabled={signatureDisabled}
                    onChange={() => {
                      handleToggle("signature");
                    }}
                  />
                </ToggleRow>

                {signatureDisabled && (
                  <Text
                    variant="text.labelXXSmall"
                    color={theme.colors.error}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                    }}
                  >
                    <InfoIcon style={{ marginTop: 2 }} />
                    Signatures are not yet supported for Moments that are not
                    your own.
                  </Text>
                )}

                <ManageSignatureLink
                  variant="text.labelSmall"
                  color="chalkAlpha60"
                  onClick={() => {
                    analytics.track("Signature - Open Modal", {
                      where: "Quick Edit",
                      type: "Manage",
                    });
                    setSignatureSelectorOpen(true);
                  }}
                >
                  <Image
                    src={`${process.env.REACT_APP_S3_CLIP_BUCKET}/static/loadouts/sig.svg`}
                    height={12}
                    width={12}
                    mr="6px"
                  />
                  Manage My Signature
                </ManageSignatureLink>
              </ToggleColumn>
              <ToggleColumn>
                <ToggleRow>
                  <FeatureTooltip
                    title="Add Signature Music"
                    description="If you have music genres selected in your Signature, we'll choose a matching song to add to your clips."
                  >
                    <Flex alignItems="center" style={{ gap: 4 }}>
                      <Text variant="text.labelSmall">Add Signature Music</Text>
                      <InfoTooltipIcon />
                    </Flex>
                  </FeatureTooltip>

                  <Toggle
                    width={48}
                    checked={selectedFinalTouches.includes("music")}
                    disabled={!selectedFinalTouches.includes("signature")}
                    onChange={() => {
                      handleToggle("music");
                    }}
                  />
                </ToggleRow>

                {signatureDisabled && (
                  <Text
                    variant="text.labelXXSmall"
                    color={theme.colors.error}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                    }}
                  >
                    <InfoIcon style={{ marginTop: 2 }} />
                    Signatures are not yet supported for Moments that are not
                    your own.
                  </Text>
                )}

                {!signatureDisabled &&
                  !selectedFinalTouches.includes("signature") &&
                  selectedFinalTouches.includes("music") && (
                    <WarningText
                      variant="text.labelXXSmall"
                      color={theme.colors.alert}
                    >
                      Signature must be enabled to use music. Enable "Add My
                      Signature" above.
                    </WarningText>
                  )}

                {!signatureDisabled &&
                  selectedFinalTouches.includes("signature") &&
                  userHasSignatureMusic === false &&
                  selectedFinalTouches.includes("music") && (
                    <WarningText
                      variant="text.labelXXSmall"
                      color={theme.colors.alert}
                    >
                      You do not have music genres selected in your Signature.
                      Click "Manage My Signature" above to add genres.
                    </WarningText>
                  )}
              </ToggleColumn>
            </>
          )}
          {isCounterStrike && (
            <ToggleColumn>
              <ToggleRow>
                <FeatureTooltip
                  title="Add Context Cams"
                  description="Before a gunfight that ends with 1 or more kills, we'll show a raised camera view of the area, giving your viewers extra context about what happened."
                >
                  <Flex alignItems="center" style={{ gap: 4 }}>
                    <Text variant="text.labelSmall">Add Context Cams</Text>
                    <InfoTooltipIcon />
                  </Flex>
                </FeatureTooltip>
                <Toggle
                  width={48}
                  checked={selectedFinalTouches.includes("contextCams")}
                  disabled={contextCamsDisabled}
                  onChange={() => {
                    handleToggle("contextCams");
                  }}
                />
              </ToggleRow>
              {contextCamsDisabled && (
                <Text
                  variant="text.labelXXSmall"
                  color={theme.colors.error}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                  }}
                >
                  <InfoIcon style={{ marginTop: 2 }} />
                  Context Cams are not yet supported for Fail Moments.
                </Text>
              )}
            </ToggleColumn>
          )}

          {isCounterStrike && (
            <ToggleColumn>
              <ToggleRow>
                <Flex alignItems="center">
                  <StyledImage
                    src={`${REACT_APP_S3_CLIP_BUCKET}/static/svg/early-access.svg`}
                    width={40}
                    mr="12px"
                  />
                  <Flex alignItems="flex-start" flexDirection="column">
                    <FeatureTooltip
                      title="Add Camera Effects"
                      description="Enable player follow cams for more cinematic shots."
                    >
                      <Flex alignItems="center" style={{ gap: 4 }}>
                        <Text variant="text.labelSmall">
                          Add Camera Effects
                        </Text>
                        <InfoTooltipIcon />
                      </Flex>
                    </FeatureTooltip>
                  </Flex>
                </Flex>

                <Toggle
                  width={48}
                  checked={selectedFinalTouches.includes("mountedCameras")}
                  disabled={allstarUser?.user?.userGroup?.type === "FREE"}
                  onChange={() => {
                    handleToggle("mountedCameras");
                  }}
                />
              </ToggleRow>
            </ToggleColumn>
          )}

          {isCounterStrike && (
            <ToggleColumn>
              <ToggleRow>
                <Flex alignItems="center">
                  <StyledImage
                    src={`${REACT_APP_S3_CLIP_BUCKET}/static/svg/early-access.svg`}
                    width={40}
                    mr="12px"
                  />
                  <Flex alignItems="flex-start" flexDirection="column">
                    <FeatureTooltip
                      title="Final Kill Cam"
                      description="On the last kill, we'll switch views to the enemy player. BONUS: When enabled, this will hide your deaths at the end of the clip!"
                    >
                      <Flex alignItems="center" style={{ gap: 4 }}>
                        <Text variant="text.labelSmall">Final Kill Cam</Text>
                        <InfoTooltipIcon />
                      </Flex>
                    </FeatureTooltip>
                  </Flex>
                </Flex>

                <Toggle
                  width={48}
                  checked={selectedFinalTouches.includes("finalKillCamera")}
                  disabled={allstarUser?.user?.userGroup?.type === "FREE"}
                  onChange={() => {
                    handleToggle("finalKillCamera");
                  }}
                />
              </ToggleRow>
            </ToggleColumn>
          )}

          {isCounterStrike && (
            <ToggleColumn>
              <ToggleRow>
                <Flex alignItems="center">
                  <StyledImage
                    src={`${REACT_APP_S3_CLIP_BUCKET}/static/svg/early-access.svg`}
                    width={40}
                    mr="12px"
                  />
                  <Flex alignItems="flex-start" flexDirection="column">
                    <FeatureTooltip
                      title="Disable Jump Cuts"
                      description="By default, Allstar cuts your clips to the best moments, with optional transitions between them. Use this to show the full sequence from first-to-last kill. Does not work with Rapid Fire enabled."
                    >
                      <Flex alignItems="center" style={{ gap: 4 }}>
                        <Text variant="text.labelSmall">Disable Jump Cuts</Text>
                        <InfoTooltipIcon />
                      </Flex>
                    </FeatureTooltip>
                  </Flex>
                </Flex>

                <Toggle
                  width={48}
                  checked={selectedFinalTouches.includes("disableJumpCuts")}
                  disabled={
                    allstarUser?.user?.userGroup?.type === "FREE" ||
                    selectedFinalTouches.includes("rapidFire")
                  }
                  onChange={() => {
                    handleToggle("disableJumpCuts");
                  }}
                />
              </ToggleRow>
              {selectedFinalTouches.includes("rapidFire") && (
                <Text
                  variant="text.labelXXSmall"
                  color={theme.colors.error}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 4,
                    whiteSpace: "normal",
                    flexWrap: "wrap",
                  }}
                >
                  <Box style={{ display: "flex", alignItems: "center" }}>
                    <InfoIcon style={{ marginTop: 2, flexShrink: 0 }} />
                    <Box style={{ marginLeft: 6 }}>
                      Disabling Jump Cuts is not supported when Rapid Fire is
                      enabled.
                    </Box>
                  </Box>
                </Text>
              )}
            </ToggleColumn>
          )}
          {isCounterStrike && (
            <ToggleColumn>
              <ToggleRow>
                <FeatureTooltip
                  title="Rapid Fire"
                  description="Love Jump Cuts, but want something even faster? Enable Rapid Fire to see just the kills. Obviously, this does not work with Disable Jump Cuts enabled."
                >
                  <Flex alignItems="center" style={{ gap: 4 }}>
                    <Text variant="text.labelSmall">Rapid Fire</Text>
                    <InfoTooltipIcon />
                  </Flex>
                </FeatureTooltip>
                <Toggle
                  width={48}
                  checked={selectedFinalTouches.includes("rapidFire")}
                  disabled={
                    rapidFireDisabled ||
                    selectedFinalTouches.includes("disableJumpCuts")
                  }
                  onChange={() => {
                    handleToggle("rapidFire");
                  }}
                />
              </ToggleRow>
              {(rapidFireDisabled ||
                selectedFinalTouches.includes("disableJumpCuts")) && (
                <Text
                  variant="text.labelXXSmall"
                  color={theme.colors.error}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 4,
                    whiteSpace: "normal",
                    flexWrap: "wrap",
                  }}
                >
                  <Box style={{ display: "flex", alignItems: "center" }}>
                    <InfoIcon style={{ marginTop: 2, flexShrink: 0 }} />
                    <Box style={{ marginLeft: 6 }}>
                      {rapidFireDisabled
                        ? "Rapid Fire is not yet supported for Fail Moments."
                        : "Rapid Fire is not supported when Disabled Jump Cuts is enabled."}
                    </Box>
                  </Box>
                </Text>
              )}
            </ToggleColumn>
          )}
        </ToggleContainer>
      </Section>
    </>
  );
};

export const StyledImage = styled(Image)`
  @media (max-width: ${(props) => props.theme.breaks.small}) {
    display: none;
  }
`;

// Tooltip component for toggles
const FeatureTooltip = ({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
}) => {
  return (
    <Tooltip
      position="bottom: 50%; left: 50%; transform: translateX(-50%); margin-bottom: 8px;"
      align="center"
      wrapText={true}
      hoverAnimation={true}
      tooltipContent={
        <TooltipContent>
          <TooltipTitle variant="text.labelSmall" color="ice" fontWeight={600}>
            {title}
          </TooltipTitle>
          <TooltipDescription variant="text.labelXSmall" color="chalkAlpha80">
            {description}
          </TooltipDescription>
        </TooltipContent>
      }
    >
      {children}
    </Tooltip>
  );
};

const TooltipContent = styled(Flex)`
  align-items: start;
  background-color: ${theme.colors.grey850};
  border-radius: 8px;
  border: 1px solid ${theme.colors.chalkAlpha20};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  flex-direction: column;
  gap: 6px;

  padding: 14px;

  width: max-content;
`;

const TooltipTitle = styled(Text)`
  line-height: 1.2;
  text-align: left;
`;

const TooltipDescription = styled(Text)`
  line-height: 1.4;
  text-align: left;
  white-space: normal;
  max-width: 200px;
`;

const ManageSignatureLink = styled(Text)`
  align-items: center;
  cursor: pointer;
  display: flex;
  justify-content: center;
  line-height: 1.4;
  padding-top: 4px;
  padding-bottom: 4px;
  text-align: center;
  transition: color 100ms ease;

  &:hover {
    color: ${theme.colors.envy};
  }
`;

const InfoIcon = styled(TbInfoCircle)`
  color: ${theme.colors.error};
`;

const InfoTooltipIcon = styled(TbInfoCircle)`
  color: ${theme.colors.chalkAlpha60};
  cursor: pointer;
  height: 18px;
  width: 18px;
  transition:
    color 120ms cubic-bezier(0.4, 0, 0.2, 1),
    transform 180ms cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    color: ${theme.colors.envy};
    transform: scale(1.1);
  }
`;

const ToggleContainer = styled(Flex)`
  background-color: ${(props) => props.theme.colors.grey825};
  border-radius: 8px;
  flex-direction: column;
  width: 100%;
`;

const ToggleRow = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const ToggleColumn = styled(Flex)`
  align-items: center;
  border-bottom: 1px solid ${(props) => props.theme.colors.ned};
  justify-content: space-between;
  padding: 12px 24px;
  width: 100%;
  flex-direction: column;
  align-items: flex-start;
  &:last-child {
    border-bottom: none;
  }
`;

const Section = styled(BaseSection)`
  align-items: center;
  border-bottom: none;
  display: flex;
  flex-direction: column;
  gap: 12px;
  justify-content: center;
  width: 100%;
`;

const ThumbnailSection = styled(BaseSection)`
  background-color: ${theme.colors.grey850};
  border-radius: 12px;
  align-items: center;
  border-bottom: none;
  display: flex;
  flex-direction: column;
  gap: 12px;
  justify-content: center;
  width: 25vw;
  padding: 24px;
  @media (max-width: ${(props) => props.theme.breaks.small}) {
    width: 80vw;
  }
`;

const PreviewThumbnail = styled(Image)`
  border-radius: 12px;
  height: 100%;
  width: auto;
`;

const UploadIcon = styled(TbCloudUpload)`
  color: ${theme.colors.chalkAlpha80};
`;

const ThumbnailArea = styled(Flex)<{ showBorder: boolean }>`
    ${(props) => (props.showBorder ? theme.colors.chalkAlpha40 : "none")};
  align-items: center;
  aspect-ratio: 16 / 9;
  background-color: ${theme.colors.grey850};
  border-radius: 12px;
  border: 1px dashed
  cursor: pointer;
  height: 100%;
  justify-content: center;
  width: 100%;
      border: 1px dashed ${theme.colors.chalkAlpha40};
  &:hover {
    border: 1px dashed ${theme.colors.envy};
    & > svg {
      color: ${theme.colors.envy};
    }
  }
`;

export const SignatureText = styled(Text)`
  transition: color 100ms ease;
  &:hover {
    cursor: pointer;
    color: ${({ theme }) => theme.colors.envy};
  }
`;

export const RemoveText = styled(Text)`
  transition: color 100ms ease;
  &:hover {
    cursor: pointer;
    color: ${({ theme }) => theme.colors.alert};
  }
`;

const WarningText = styled(Text)`
  align-items: center;
  display: flex;
  gap: 4px;
  margin-bottom: 4px;
  margin-top: 4px;
`;
